import { useLazyQuery, useMutation } from "@apollo/client";
import { useFormik } from "formik";
import { useRef, useEffect, useState } from "react";
import * as Yup from "yup";

import { Button, getInput, Modal, Spinner } from "../../../components/ui";
import { EXPORT_ACTIVE_REPORT_JSON as exportActiveReportJSONMutation } from "../../../graphql/mutation/ActiveReport";
import { FETCH_REPORT_TEMPLATES } from "../../../graphql/query/ReportTemplate";
import { initializeActiveReports } from "../../../util/activeReportsInit";

const ExportActiveReport = props => {
  const {
    closeModal,
    isExportReportOpen = false,
    ids,
    listId,
    obj = {},
    optimise = false,
    search,
    select,
    type = "Invoice",
  } = props || {};

  const [Viewer, setViewer] = useState(null);
  const [onExport, setOnExport] = useState(null);
  const [isLoadingActiveReports, setIsLoadingActiveReports] = useState(false);

  const [
    fetchReportTemplatesLazily,
    {
      loading: reportTemplatesLoading,
      data: { reportTemplates: { edges = [] } = {} } = {},
    },
  ] = useLazyQuery(FETCH_REPORT_TEMPLATES, {
    variables: { input: { limit: null, type: type?.toLowerCase() } },
  });

  // Load ActiveReports when modal opens
  useEffect(() => {
    if (isExportReportOpen && !Viewer) {
      setIsLoadingActiveReports(true);
      Promise.all([
        initializeActiveReports(),
        import("@grapecity/activereports-react").then(mod => mod.Viewer),
        import("@grapecity/activereports/styles/ar-js-ui.css"),
        import("@grapecity/activereports/styles/ar-js-viewer.css"),
        import("../../../components/Functional/ActiveReport").then(
          mod => mod.onExport,
        ),
      ])
        .then(([, ViewerComponent, , , onExportFn]) => {
          setViewer(() => ViewerComponent);
          setOnExport(() => onExportFn);
          setIsLoadingActiveReports(false);
        })
        .catch(error => {
          console.error("Failed to load ActiveReports:", error);
          setIsLoadingActiveReports(false);
        });
    }
  }, [isExportReportOpen, Viewer]);

  useEffect(() => {
    if (isExportReportOpen) {
      fetchReportTemplatesLazily();
    }
  }, [isExportReportOpen]);

  const [exportActiveReportJSON, { loading }] = useMutation(
    exportActiveReportJSONMutation({ ...obj, __typename: type }),
  );
  const viewerRef = useRef();

  const formik = useFormik({
    initialValues: {},
    validationSchema: Yup.object({
      reportTemplateId: Yup.number().required(
        "Please select a report template",
      ),
    }),
    onSubmit: async values => {
      const selectedTemplate = edges?.find(
        item => item?.id === values?.reportTemplateId,
      );

      let templateFields = obj;
      if (optimise) {
        const extractResult = extractFieldsByDataset(selectedTemplate);
        const queryObject = buildNestedQueryObject(extractResult);
        templateFields = mergeSelectionTrees(queryObject, obj);
      }

      const result = await exportActiveReportJSON?.({
        mutation: exportActiveReportJSONMutation({
          ...templateFields,
          __typename: type,
        }),
        variables: {
          input: {
            type,
            ...(select === "all"
              ? { ...(listId ? { listId } : { search }) }
              : { ids }),
          },
        },
      });
      const data = {
        [type?.toLowerCase()]: result?.data?.["exportActiveReportJSON"]?.edges,
      };
      const title =
        data[type?.toLowerCase()].length > 1
          ? `${selectedTemplate.name}-${new Date().toISOString()}`
          : `${selectedTemplate.name}-${data[type?.toLowerCase()][0].id}`;

      if (onExport) {
        await onExport({
          data,
          definition: selectedTemplate?.meta?.definition,
          name: type,
          title,
          viewerRef,
        });
      }
    },
  });

  const inputs = [
    {
      label: "Report Template",
      name: "reportTemplateId",
      options: edges?.map(item => ({ label: item.name, value: item.id })),
      type: "multi-select",
    },
  ];

  const hasErrors = Object.keys(formik?.errors || {}).length > 0;

  const exportsSettings = {
    pdf: {
      title: "ActiveReportsJS Sample",
      author: "GrapeCity",
      subject: "Web Reporting",
      keywords: "reporting, sample",
      userPassword: "pwd",
      ownerPassword: "ownerPwd",
      printing: "none",
      copying: false,
      modifying: false,
      annotating: false,
      contentAccessibility: false,
      documentAssembly: false,
      pdfVersion: "1.7",
      autoPrint: true,
      filename: "ActiveReportsJS-Sample.pdf",
    },
    html: {
      title: "ActiveReportsJS Sample",
      filename: "ActiveReportsJS-Sample.html",
      autoPrint: true,
      multiPage: true,
      embedImages: "external",
      outputType: "html",
    },
  };

  // remove items from this array to hide unnecessary Exports Types
  const availableExports = ["pdf", "html", "tabular-data"];

  const previewModalProps = {
    body: () => (
      <div id="viewer-host" className="h-full w-full">
        {Viewer ? (
          <Viewer
            ref={viewerRef}
            exportsSettings={exportsSettings}
            availableExports={availableExports}
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <Spinner />
          </div>
        )}
      </div>
    ),
    disabled: loading || hasErrors || !Viewer || isLoadingActiveReports,
    closeOnBackdrop: false,
    scale: "md",
    title: "Preview",
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex flex-row">
        <div className="flex flex-1 flex-col text-2xl font-bold">
          <div>Export {type} Report</div>
        </div>
        <div className="flex gap-4">
          <Button action="default" label="Cancel" onClick={closeModal} />
          <Modal {...previewModalProps}>
            <Button
              label={"Export"}
              disabled={loading || hasErrors}
              loading={loading}
              onClick={formik.submitForm}
            />
          </Modal>
        </div>
      </div>
      {reportTemplatesLoading || isLoadingActiveReports ? (
        <div className="flex h-[30dvh] w-full items-center justify-center ">
          <Spinner />
        </div>
      ) : (
        <div className="w-50 grid grid-cols-2 gap-x-16 gap-y-4">
          {inputs?.map((item, index) => {
            const inputProps = {
              ...item,
              formik,
            };
            return <div key={index}>{getInput(inputProps)}</div>;
          })}
        </div>
      )}
    </div>
  );
};

export default ExportActiveReport;

/**
 * Extracts data fields used by an Active Reports template
 *
 * @param {*} jsonData  JSON data from active reports template definition
 * @returns
 */
export function extractFieldsByDataset(jsonData) {
  const datasetFields = {};
  const dataSourceMap = {};
  const datasetFieldsMap = {}; // Track which fields belong to which dataset

  // First pass: build mapping between dataset names and their Query.DataSourceName
  // Also build a map of which fields each dataset defines
  function buildDataSourceMap(obj) {
    if (typeof obj === "object" && obj !== null) {
      if (Array.isArray(obj)) {
        obj.forEach(item => buildDataSourceMap(item));
      } else {
        // Check for DataSets array
        if (obj.DataSets && Array.isArray(obj.DataSets)) {
          obj.DataSets.forEach(dataset => {
            if (dataset.Name && dataset.Query && dataset.Query.DataSourceName) {
              dataSourceMap[dataset.Name] = dataset.Query.DataSourceName;

              // Track fields defined by this dataset
              if (dataset.Fields && Array.isArray(dataset.Fields)) {
                datasetFieldsMap[dataset.Name] = new Set(
                  dataset.Fields.map(f => f.Name || f.DataField),
                );
              }
            }
          });
        }

        Object.values(obj).forEach(value => buildDataSourceMap(value));
      }
    }
  }

  function traverseObject(obj, currentDataset = null) {
    if (typeof obj === "object" && obj !== null) {
      // Check if this object has a DataSetName
      if (obj.DataSetName) {
        currentDataset = obj.DataSetName;
        if (!datasetFields[currentDataset]) {
          datasetFields[currentDataset] = new Set();
        }
      }

      if (Array.isArray(obj)) {
        obj.forEach(item => traverseObject(item, currentDataset));
      } else {
        Object.entries(obj).forEach(([key, value]) => {
          // Check for Fields expressions in Value or FilterExpression
          if (
            (key === "Value" || key === "FilterExpression") &&
            typeof value === "string" &&
            value.includes("Fields")
          ) {
            // Determine which dataset to add fields to
            let targetDataset = currentDataset;

            // If no current dataset, add to a special root dataset marker
            if (!targetDataset) {
              targetDataset = "__ROOT__";
              if (!datasetFields[targetDataset]) {
                datasetFields[targetDataset] = new Set();
              }
            }

            // Extract direct field references (Fields!fieldName.Value)
            const directFieldMatches = value.match(/Fields!(\w+)\.Value/g);
            if (directFieldMatches) {
              directFieldMatches.forEach(match => {
                const field = match.match(/Fields!(\w+)\.Value/)[1];
                datasetFields[targetDataset].add(field);
              });
            }

            // Extract nested field references (Fields.Item("field.path").Value)
            const nestedFieldMatches = value.match(
              /Fields\.Item\("([^"]+)"\)/g,
            );
            if (nestedFieldMatches) {
              nestedFieldMatches.forEach(match => {
                const field = match.match(/Fields\.Item\("([^"]+)"\)/)[1];
                datasetFields[targetDataset].add(field);
              });
            }
          }

          traverseObject(value, currentDataset);
        });
      }
    }
  }

  // Helper function to resolve dataset references to root datasources
  // e.g., "$dataset:DataSet/Field1" where Field1 is the ONLY/PRIMARY field in DataSet
  // and represents the actual entity (common pattern in ActiveReports)
  function resolveDataSourceReference(dataSourceName) {
    if (!dataSourceName || !dataSourceName.startsWith("$dataset:")) {
      return dataSourceName;
    }

    const pathPart = dataSourceName.replace("$dataset:", "");
    const segments = pathPart.split("/");

    // Check if the last segment is actually a field of the parent dataset
    if (segments.length >= 2) {
      const parentDatasetName = segments[segments.length - 2];
      const possibleFieldName = segments[segments.length - 1];

      // If the parent dataset has this field defined
      if (datasetFieldsMap[parentDatasetName]?.has(possibleFieldName)) {
        const parentFields = datasetFieldsMap[parentDatasetName];

        // Only resolve if this is the ONLY field or a generic field name pattern
        // This indicates it's a field reference pattern, not a relationship
        const isOnlyField = parentFields.size === 1;
        const isGenericFieldName = /^Field\d*$/i.test(possibleFieldName);

        if (isOnlyField || isGenericFieldName) {
          // This is a field reference pattern, not a dataset reference
          // Resolve to the root datasource of the parent
          const parentDataSource = dataSourceMap[parentDatasetName];
          if (parentDataSource && !parentDataSource.startsWith("$dataset:")) {
            // Parent is a root datasource, return it
            return parentDataSource;
          } else if (parentDataSource) {
            // Parent is also a reference, recurse
            return resolveDataSourceReference(parentDataSource);
          }
        }
      }
    }

    return dataSourceName;
  }

  // Build the data source mapping first
  buildDataSourceMap(jsonData);

  // Then traverse for field extraction
  traverseObject(jsonData);

  // Convert Sets to Arrays and create nested objects, using Query.DataSourceName for key mapping
  const result = {};
  Object.entries(datasetFields).forEach(([dataset, fieldsSet]) => {
    // Skip the special __ROOT__ marker - it will be handled separately
    if (dataset === "__ROOT__") {
      return;
    }

    // Get the Query.DataSourceName for this dataset
    const dataSourceName = dataSourceMap[dataset];

    // Resolve the datasource to handle field references like "$dataset:DataSet/Field1"
    const resolvedDataSourceName = resolveDataSourceReference(dataSourceName);
    const isResolvedToRoot =
      resolvedDataSourceName !== dataSourceName &&
      resolvedDataSourceName &&
      !resolvedDataSourceName.startsWith("$dataset:");

    // Determine the result key based on Query.DataSourceName
    let resultKey = dataset;
    if (dataSourceName && dataSourceName.startsWith("$dataset:")) {
      // Extract the GraphQL field path from DataSourceName
      const pathPart = dataSourceName.replace("$dataset:", "");
      const segments = pathPart.split("/");
      if (segments.length > 1) {
        resultKey = segments[segments.length - 1];
      }
    }

    result[resultKey] = {
      fields: Array.from(fieldsSet).sort(),
      fieldsObject: Array.from(fieldsSet).reduce((obj, field) => {
        // Convert dot notation to nested object structure
        const parts = field.split(".");
        let current = obj;

        for (let i = 0; i < parts.length - 1; i++) {
          if (!current[parts[i]]) {
            current[parts[i]] = {};
          }
          current = current[parts[i]];
        }

        current[parts[parts.length - 1]] = true;
        return obj;
      }, {}),
    };

    // Add dataSourceName as metadata for debugging/optimization
    if (dataSourceName) {
      result[resultKey].dataSourceName = dataSourceName;
    }

    // If this resolved to a root datasource, mark it for merging
    if (isResolvedToRoot) {
      result[resultKey].resolvedDataSourceName = resolvedDataSourceName;
      result[resultKey].shouldMergeToRoot = true;
    }
  });

  // Add referenced datasets that have no direct fields
  Object.entries(result).forEach(([, datasetInfo]) => {
    const { dataSourceName } = datasetInfo;
    if (!dataSourceName) {
      return;
    }
    const pathPart = dataSourceName.replace("$dataset:", "");
    const segments = pathPart.split("/");
    if (segments[0] in result || segments[0] === "DataSet") {
      return;
    }
    const dataSourceRef = dataSourceMap[segments[0]];
    if (dataSourceRef) {
      result[segments[0]] = { dataSourceName: dataSourceRef };
    }
  });

  // Add root datasets that were found in DataSets but have no field references
  Object.entries(dataSourceMap).forEach(([datasetName, dataSourceName]) => {
    // Check if this is a root dataset (DataSourceName without $dataset: prefix)
    if (!dataSourceName.startsWith("$dataset:")) {
      // Determine the result key - for root datasets, use the dataset name
      let resultKey = datasetName;

      // If this dataset doesn't already exist in results, add it
      if (!result[resultKey]) {
        result[resultKey] = {
          dataSourceName,
          fields: [],
          fieldsObject: {},
        };
      }

      // Collect all fields that should be merged into this root dataset
      const fieldsToMerge = [];

      // 1. Add __ROOT__ fields
      if (datasetFields["__ROOT__"]) {
        fieldsToMerge.push(...Array.from(datasetFields["__ROOT__"]));
      }

      // 2. Add fields from datasets that resolved to this root datasource
      Object.entries(result).forEach(([key, datasetInfo]) => {
        if (
          datasetInfo.shouldMergeToRoot &&
          datasetInfo.resolvedDataSourceName === dataSourceName &&
          key !== resultKey
        ) {
          // Merge fields from this dataset into root
          if (datasetInfo.fields && datasetInfo.fields.length > 0) {
            fieldsToMerge.push(...datasetInfo.fields);
          }
        }
      });

      if (fieldsToMerge.length > 0) {
        const uniqueFields = Array.from(new Set(fieldsToMerge)).sort();
        result[resultKey].fields = [
          ...result[resultKey].fields,
          ...uniqueFields,
        ].sort();

        // Create nested object structure for merged fields
        const mergedFieldsObject = uniqueFields.reduce((obj, field) => {
          const parts = field.split(".");
          let current = obj;

          for (let i = 0; i < parts.length - 1; i++) {
            if (!current[parts[i]]) {
              current[parts[i]] = {};
            }
            current = current[parts[i]];
          }

          current[parts[parts.length - 1]] = true;
          return obj;
        }, {});

        // Merge fields object with existing fields object
        result[resultKey].fieldsObject = {
          ...result[resultKey].fieldsObject,
          ...mergedFieldsObject,
        };
      }
    }
  });

  // Remove datasets that were merged to root
  Object.keys(result).forEach(key => {
    if (result[key].shouldMergeToRoot) {
      delete result[key];
    }
  });

  return result;
}

/**
 * Helper to check if a value is a plain object (not null, not array)
 */
const isPlainObject = value =>
  value !== null && typeof value === "object" && !Array.isArray(value);

/**
 * Merges two nested selection trees (GraphQL-style objects of booleans/objects).
 * The base tree comes from template analysis, overrides come from explicit user config.
 * Recursively combines both, preserving all fields from both sources.
 *
 * @param {Object} base - Base selection tree (from template)
 * @param {Object} overrides - Override selection tree (from user config)
 * @returns {Object} Merged selection tree
 */
function mergeSelectionTrees(base, overrides) {
  if (!isPlainObject(overrides)) {
    return isPlainObject(base) ? { ...base } : base ?? {};
  }

  const result = isPlainObject(base) ? { ...base } : {};

  Object.entries(overrides).forEach(([key, overrideValue]) => {
    if (isPlainObject(overrideValue)) {
      const baseValue = result[key];
      result[key] = mergeSelectionTrees(
        isPlainObject(baseValue) ? baseValue : {},
        overrideValue,
      );
    } else {
      result[key] = overrideValue;
    }
  });

  return result;
}

/**
 * Builds a nested GraphQL query structure from extractFieldsByDataset output
 * using the dataSourceName paths to create proper nesting. The first segment
 * after $dataset: is always skipped to flatten the structure.
 *
 * @param {Object} extractedData - Output from extractFieldsByDataset function
 * @returns {Object} Nested object structure matching GraphQL query format
 */
export function buildNestedQueryObject(extractedData, root) {
  const result = {};

  // Extract root entity from the dataset with Name: "DataSet"
  let rootEntity;
  Object.entries(extractedData).forEach(([, datasetInfo]) => {
    const { dataSourceName } = datasetInfo;
    // Look for the root dataset which has DataSourceName without $dataset: prefix
    // This corresponds to { "Name": "DataSet", "Query": { "DataSourceName": "Invoice" } }
    if (dataSourceName && !dataSourceName.startsWith("$dataset:")) {
      rootEntity = dataSourceName.toLowerCase();
    }
  });

  // Collect all dataset definitions to identify datasets that can be referenced
  const datasetDefinitions = new Set();
  Object.entries(extractedData).forEach(([, datasetInfo]) => {
    const { dataSourceName } = datasetInfo;
    if (dataSourceName && dataSourceName.startsWith("$dataset:")) {
      const pathPart = dataSourceName.replace("$dataset:", "");
      const segments = pathPart.split("/");

      // Only consider as referenceable if it's a child of a root entity
      // e.g., "$dataset:instance/warehouseItem" -> warehouseItem can be referenced
      if (
        segments.length >= 2 &&
        segments[segments.length - 1] !== rootEntity
      ) {
        const childDataset = segments[segments.length - 1];
        datasetDefinitions.add(childDataset);
      }
    }
  });

  Object.entries(extractedData).forEach(([, datasetInfo]) => {
    const { dataSourceName, fieldsObject } = datasetInfo;

    if (!dataSourceName || !fieldsObject) {
      return;
    }

    // Handle different dataSourceName formats
    if (dataSourceName.startsWith("$dataset:")) {
      // Parse $dataset:parent/child format
      const pathPart = dataSourceName.replace("$dataset:", "");
      const segments = pathPart.split("/");

      if (segments.length === 1) {
        // Single segment path (shouldn't happen with current data)
        const fieldName = segments[0];
        if (fieldName !== "DataSet") {
          result[fieldName] = fieldsObject;
        }
      } else if (segments.length === 2 && segments[0] === "DataSet") {
        // Pattern: "$dataset:DataSet/anyEntity"
        const entityName = segments[1];
        // If this matches the rootEntity (extracted from DataSet definitions), merge into root
        // Otherwise, create nested object for child entities like 'items', 'paymentPlan'
        if (entityName === rootEntity) {
          Object.assign(result, fieldsObject);
        } else {
          result[entityName] = fieldsObject;
        }
      } else if (segments.length >= 2) {
        // Nested path (e.g., "$dataset:invoice/paymentPlan" or "$dataset:warehouseItem/warehouseMoves")
        let current = result;
        let startIndex = 0;

        // Check if the first segment is a referenced dataset
        const firstSegment = segments[0];
        if (datasetDefinitions.has(firstSegment)) {
          // This is a reference to a defined dataset (e.g., "$dataset:warehouseItem/warehouseMoves")
          // Keep the first segment as part of the path
          startIndex = 0;

          // Ensure the parent dataset object exists
          if (!result[firstSegment]) {
            result[firstSegment] = {};
          }
        } else if (firstSegment === "DataSet") {
          // Skip DataSet and the next segment
          startIndex = 2;
        } else {
          // Skip the first segment (e.g., "invoice", "instance")
          startIndex = 1;
        }

        // Navigate through remaining segments except the last one
        for (let i = startIndex; i < segments.length - 1; i++) {
          const segment = segments[i];
          if (!current[segment]) {
            current[segment] = {};
          }
          current = current[segment];
        }

        // Set the fieldsObject at the final path
        const finalKey = segments[segments.length - 1];
        current[finalKey] = fieldsObject;
      }
    } else {
      // Merge fields from root-level data sources like "Invoice" directly into result
      Object.assign(result, fieldsObject);
    }
  });

  return result;
}
